import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { EmployerOnboardingPage, useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { Modal } from 'slab'
import {
  getGetQuotingProspectsProspectIdQuotesIdGenerationOptionsQueryKey as getQuoteGenerationOptionsQueryKey,
  getGetQuotingProspectsProspectIdQuotesQueryKey as getQuotesQueryKey,
  getQuotingProspectsProspectIdQuotesIdGenerationOptions as getQuoteGenerationOptions,
  usePostQuotingProspectsProspectIdQuotesIdGenerate as useGenerateQuote,
} from 'utils/web_api'

import { useEmployer } from '../EmployerProvider'
import { useQuote } from '../QuoteProvider'

interface OutdatedConfigModalProps {
  quoteYear: number | null
}

export function OutdatedConfigModal ({ quoteYear = new Date().getFullYear() + 1 }: OutdatedConfigModalProps) {
  const { employer } = useEmployer()
  const { quote, shouldShowPendingPricesWarning } = useQuote()
  const { navigateToStep } = useOnboardingStep()
  const queryClient = useQueryClient()
  const generateQuoteMutation = useGenerateQuote()

  const [showOutdatedConfigModal, setShowOutdatedConfigModal] = useState(false)

  useEffect(() => {
    if (quote?.outdated_granular_allowance_config && shouldShowPendingPricesWarning) {
      setShowOutdatedConfigModal(true)
    }
  }, [quote?.outdated_granular_allowance_config])

  const handleRegenerateQuote = async () => {
    try {
      const generationOptions = await queryClient.fetchQuery({
        queryKey: getQuoteGenerationOptionsQueryKey(employer!.quoting_prospect_id!, quote!.id),
        queryFn: () => getQuoteGenerationOptions(employer!.quoting_prospect_id!, quote!.id)
      })

      await generateQuoteMutation.mutateAsync({
        prospectId: employer!.quoting_prospect_id!,
        id: quote!.id,
        data: {
          coverage_percentiles: generationOptions.coverage_percentiles,
          coverage_start_date: generationOptions.coverage_start_date,
          metal_tiers: generationOptions.metal_tiers,
          year: generationOptions.quote_year,
        }
      })

      await queryClient.invalidateQueries({
        queryKey: getQuotesQueryKey(employer!.quoting_prospect_id!, undefined)
      })

      setShowOutdatedConfigModal(false)
      navigateToStep(EmployerOnboardingPage.QUOTE)
    } catch (error) {
      console.error('Failed to regenerate quote:', error)
    }
  }

  return (
    <Modal open={showOutdatedConfigModal} onOpenChange={setShowOutdatedConfigModal}>
      <Modal.Body>
        <Modal.Title dismissible={false}>Newer {quoteYear} data available</Modal.Title>
        <Modal.Description>
          We've received new finalized plan data for the state(s) you have employees in. Refresh your quote to get our most updated cost estimates.
        </Modal.Description>
      </Modal.Body>
      <Modal.Footer>
        <div className='flex justify-end'>
          <Modal.Button variant='primary' onClick={handleRegenerateQuote} disabled={generateQuoteMutation.isPending}>
            Refresh quote
          </Modal.Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
